// src/pages/Social.jsx
// Módulo Social Publisher — imágenes desde Google Drive

import { useState, useEffect, useCallback, useRef } from 'react';

const API = 'https://bot.devit.es/api';
function getToken() { return localStorage.getItem('devit_token'); }
function authHeaders() {
  return { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' };
}

function truncNombre(nombre, max = 18) {
  if (!nombre || nombre.length <= max) return nombre;
  const dot = nombre.lastIndexOf('.');
  const ext = dot > -1 ? nombre.slice(dot) : '';
  const base = dot > -1 ? nombre.slice(0, dot) : nombre;
  return `${base.slice(0, max - ext.length - 3)}...${ext}`;
}

// ─── UI base ────────────────────────────────────────────────────────────────

function Badge({ children, color = 'green' }) {
  const colors = {
    green:  { bg: '#1a3a2a', text: '#4ade80', border: '#2a5a3a' },
    red:    { bg: '#3a1a1a', text: '#f87171', border: '#5a2a2a' },
    blue:   { bg: '#1a2a3a', text: '#60a5fa', border: '#2a3a5a' },
    yellow: { bg: '#3a2e1a', text: '#fbbf24', border: '#5a4a2a' },
    gray:   { bg: '#2a2e4a', text: '#9b9fc4', border: '#3a3e5a' },
    purple: { bg: '#2e1a3a', text: '#c4b5fd', border: '#4a2a5a' },
  };
  const c = colors[color] || colors.gray;
  return (
    <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`,
      borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
      {children}
    </span>
  );
}

function Btn({ children, onClick, variant = 'primary', disabled = false, style = {} }) {
  const styles = {
    primary:   { background: '#7c3aed', color: '#fff', border: 'none' },
    secondary: { background: '#1a1d2e', color: '#9b9fc4', border: '1px solid #2a2e4a' },
    danger:    { background: '#7f1d1d', color: '#fca5a5', border: 'none' },
    ghost:     { background: 'transparent', color: '#9b9fc4', border: '1px solid #2a2e4a' },
    teal:      { background: '#0f3a35', color: '#2dd4bf', border: '1px solid #1a5a50' },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...styles[variant], padding: '8px 18px', borderRadius: 8,
      cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600,
      opacity: disabled ? 0.5 : 1, transition: 'opacity 0.15s', ...style,
    }}>
      {children}
    </button>
  );
}

function Card({ children, style = {}, id }) {
  return (
    <div id={id} style={{ background: '#1a1d2e', border: '1px solid #2a2e4a',
      borderRadius: 12, padding: 24, ...style }}>
      {children}
    </div>
  );
}

function Input({ label, value, onChange, placeholder, type = 'text', rows }) {
  const base = {
    width: '100%', background: '#12141f', border: '1px solid #2a2e4a',
    borderRadius: 8, padding: '10px 14px', color: '#f0f0ff', fontSize: 14,
    outline: 'none', boxSizing: 'border-box',
  };
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ display: 'block', color: '#9b9fc4', fontSize: 13, marginBottom: 6 }}>{label}</label>}
      {rows
        ? <textarea value={value} onChange={e => onChange(e.target.value)}
            placeholder={placeholder} rows={rows} style={{ ...base, resize: 'vertical' }} />
        : <input type={type} value={value} onChange={e => onChange(e.target.value)}
            placeholder={placeholder} style={base} />
      }
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ display: 'block', color: '#9b9fc4', fontSize: 13, marginBottom: 6 }}>{label}</label>}
      <select value={value} onChange={e => onChange(e.target.value)} style={{
        width: '100%', background: '#12141f', border: '1px solid #2a2e4a',
        borderRadius: 8, padding: '10px 14px', color: '#f0f0ff', fontSize: 14,
      }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function PlatformToggle({ selected, onChange }) {
  const platforms = [
    { id: 'linkedin', label: 'LinkedIn', icon: '💼' },
    { id: 'facebook', label: 'Facebook', icon: '📘' },
    { id: 'instagram', label: 'Instagram', icon: '📸' },
  ];
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', color: '#9b9fc4', fontSize: 13, marginBottom: 8 }}>Plataformas</label>
      <div style={{ display: 'flex', gap: 10 }}>
        {platforms.map(p => {
          const active = selected.includes(p.id);
          return (
            <button key={p.id} onClick={() => {
              if (active) onChange(selected.filter(x => x !== p.id));
              else onChange([...selected, p.id]);
            }} style={{
              flex: 1, padding: '10px 0', borderRadius: 8, cursor: 'pointer',
              fontWeight: 600, fontSize: 13,
              background: active ? '#3b0764' : '#12141f',
              border: `1px solid ${active ? '#7c3aed' : '#2a2e4a'}`,
              color: active ? '#e9d5ff' : '#9b9fc4', transition: 'all 0.15s',
            }}>
              {p.icon} {p.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MediaImageInput({ value, onChange, plataformas }) {
  const [imagenSel, setImagenSel] = useState(null); // { nombre, url } de la imagen elegida
  const [pickerOpen, setPickerOpen] = useState(false);
  const tieneInstagram = plataformas ? plataformas.includes('instagram') : false;

  // Resolver la miniatura de la imagen ya seleccionada (por nombre) al montar / cambiar value
  useEffect(() => {
    if (!value) { setImagenSel(null); return; }
    fetch(`${API}/tenants/media/`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        const img = d?.imagenes?.find(i => i.nombre === value);
        setImagenSel(img || { nombre: value, url: null });
      })
      .catch(() => setImagenSel({ nombre: value, url: null }));
  }, [value]);

  function handleElegir(img) {
    onChange(img.nombre);
    setImagenSel(img);
    setPickerOpen(false);
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', color: '#9b9fc4', fontSize: 13, marginBottom: 8 }}>
        🖼️ Imagen del post
        {tieneInstagram && <span style={{ marginLeft: 8, color: '#f87171', fontSize: 11, fontWeight: 600 }}>⚠ Obligatorio para Instagram</span>}
        {!tieneInstagram && <span style={{ marginLeft: 8, color: '#5a5e82', fontSize: 11 }}>Opcional para LinkedIn y Facebook</span>}
      </label>

      {imagenSel ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#12141f', border: '1px solid #2a2e4a', borderRadius: 8, padding: 8 }}>
          {imagenSel.url && (
            <img src={imagenSel.url} alt={imagenSel.nombre} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
          )}
          <span title={imagenSel.nombre} style={{ flex: 1, color: '#f0f0ff', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {imagenSel.nombre}
          </span>
          <button onClick={() => setPickerOpen(true)}
            style={{ background: '#1a1c2e', border: '1px solid #2a2e4a', borderRadius: 6, padding: '6px 12px', color: '#c4b5fd', cursor: 'pointer', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
            Cambiar
          </button>
          <button onClick={() => { onChange(''); setImagenSel(null); }} title="Quitar imagen"
            style={{ background: 'none', border: 'none', color: '#5a5e82', cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>
            ✕
          </button>
        </div>
      ) : (
        <button onClick={() => setPickerOpen(true)} style={{
          display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          background: '#1a1c2e', border: '1px dashed #2a2e4a', borderRadius: 8,
          padding: '9px 16px', color: '#9b9fc4', fontSize: 13, width: '100%', justifyContent: 'center' }}>
          🖼️ Elegir imagen de la galería
        </button>
      )}

      {pickerOpen && (
        <GalleryPickerModal
          seleccionActual={value}
          onElegir={handleElegir}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

function GalleryPickerModal({ seleccionActual, onElegir, onClose }) {
  const [imagenes, setImagenes] = useState([]);
  const [cuota, setCuota]       = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [progreso, setProgreso] = useState(null);
  const [error, setError]       = useState('');
  const fileRef = useRef();

  const cargarImagenes = useCallback(async () => {
    try {
      const r = await fetch(`${API}/tenants/media/`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (r.ok) {
        const d = await r.json();
        setImagenes(d.imagenes || []);
        setCuota({ uso: d.uso_mb, limite: d.limite_mb, libre: d.libre_mb });
      }
    } catch {}
  }, []);

  useEffect(() => { cargarImagenes(); }, [cargarImagenes]);

  async function subirUna(file) {
    const b64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const r = await fetch(`${API}/tenants/media/subir`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ file_b64: b64, nombre_original: file.name }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.detail || 'Error al subir');
    return d;
  }

  async function handleFiles(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setSubiendo(true); setError('');
    let ultima = null;
    for (let i = 0; i < files.length; i++) {
      setProgreso({ actual: i + 1, total: files.length });
      try { ultima = await subirUna(files[i]); }
      catch (err) { setError(err.message); }
    }
    setProgreso(null);
    setSubiendo(false);
    fileRef.current.value = '';
    await cargarImagenes();
    if (ultima) onElegir({ nombre: ultima.nombre, url: ultima.url });
  }

  async function eliminarImagen(nombre, e) {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar "${nombre}" de la galería? Esta acción no se puede deshacer.`)) return;
    await fetch(`${API}/tenants/media/${encodeURIComponent(nombre)}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } });
    await cargarImagenes();
  }

  const pct = cuota ? Math.min(100, Math.round((cuota.uso / cuota.limite) * 100)) : 0;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#1a1d2e', border: '1px solid #2a2e4a', borderRadius: 14, padding: 24,
        width: '100%', maxWidth: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ color: '#f0f0ff', fontSize: 16 }}>🖼️ Elegir imagen</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#9b9fc4', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        {cuota && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: cuota.libre < 20 ? '#f87171' : '#5a5e82', marginBottom: 4 }}>
              <span>Espacio usado</span><span>{cuota.uso}MB / {cuota.limite}MB</span>
            </div>
            <div style={{ height: 3, background: '#1e2035', borderRadius: 2 }}>
              <div style={{ height: '100%', width: `${pct}%`, background: pct > 80 ? '#f87171' : '#7c4dff', borderRadius: 2 }} />
            </div>
          </div>
        )}

        <div style={{ overflowY: 'auto', flex: 1, marginBottom: 14 }}>
          {imagenes.length === 0 ? (
            <p style={{ color: '#5a5e82', fontSize: 13, textAlign: 'center', padding: '30px 0' }}>
              No tienes imágenes subidas aún. Sube la primera.
            </p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {imagenes.map(img => (
                <div key={img.nombre} title={img.nombre} onClick={() => onElegir(img)}
                  style={{ position: 'relative', cursor: 'pointer', borderRadius: 8, overflow: 'hidden',
                    border: `2px solid ${seleccionActual === img.nombre ? '#7c4dff' : '#2a2e4a'}`,
                    width: 84, height: 84, flexShrink: 0 }}>
                  <img src={img.url} alt={img.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button onClick={e => eliminarImagen(img.nombre, e)}
                    style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.6)',
                      border: 'none', borderRadius: 4, color: '#fff', fontSize: 10, padding: '1px 4px', cursor: 'pointer' }}>
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          background: '#1a1c2e', border: '1px dashed #2a2e4a', borderRadius: 8,
          padding: '9px 16px', color: '#9b9fc4', fontSize: 13, justifyContent: 'center' }}>
          {subiendo ? `⏳ Subiendo ${progreso?.actual || 0}/${progreso?.total || 0}...` : '+ Subir imagen(es)'}
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} style={{ display: 'none' }} disabled={subiendo} />
        </label>
        {error && <div style={{ marginTop: 8, fontSize: 12, color: '#f87171' }}>{error}</div>}
      </div>
    </div>
  );
}

// ─── Tab: Generar + Publicar ─────────────────────────────────────────────────

function TabGenerar({ status }) {
  const [tema, setTema]               = useState('');
  const [plataforma, setPlataforma]   = useState('linkedin');
  const [tono, setTono]               = useState('profesional');
  const [contenido, setContenido]     = useState('');
  const [plataformas, setPlataformas] = useState(['linkedin']);
  const [imagenNombre, setImagenNombre] = useState('');
  const [loading, setLoading]         = useState(false);
  const [publishing, setPublishing]   = useState(false);
  const [msg, setMsg]                 = useState(null);

  async function handleGenerar() {
    if (!tema.trim()) return;
    setLoading(true); setMsg(null);
    try {
      const r = await fetch(`${API}/social/generate`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ tema, plataforma, tono }),
      });
      const d = await r.json();
      if (r.ok) {
        setContenido(d.contenido);
        setMsg({ type: 'ok', text: `✅ Contenido generado (${d.tokens_used} tokens)` });
      } else {
        setMsg({ type: 'err', text: d.detail || 'Error generando contenido' });
      }
    } catch { setMsg({ type: 'err', text: 'Error de conexión' }); }
    setLoading(false);
  }

  async function handlePublicar() {
    if (!contenido.trim() || plataformas.length === 0) return;
    setPublishing(true); setMsg(null);
    try {
      const r = await fetch(`${API}/social/publish`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ contenido, plataformas, imagen_nombre: imagenNombre.trim() || null }),
      });
      const d = await r.json();
      if (r.ok) setMsg({ type: 'ok', text: `✅ Publicado en ${d.publicados}/${d.total} plataformas` });
      else setMsg({ type: 'err', text: d.detail || 'Error publicando' });
    } catch { setMsg({ type: 'err', text: 'Error de conexión' }); }
    setPublishing(false);
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      <Card>
        <h3 style={{ color: '#f0f0ff', marginBottom: 20, fontSize: 16 }}>🤖 Generar contenido</h3>
        <Input label="Tema del post" value={tema} onChange={setTema}
          placeholder="Ej: Ventajas de usar IA en atención al cliente" />
        <Select label="Plataforma de generación" value={plataforma} onChange={setPlataforma}
          options={[
            { value: 'linkedin',  label: '💼 LinkedIn' },
            { value: 'facebook',  label: '📘 Facebook' },
            { value: 'instagram', label: '📸 Instagram' },
          ]} />
        <Select label="Tono" value={tono} onChange={setTono}
          options={[
            { value: 'profesional', label: 'Profesional' },
            { value: 'inspirador',  label: 'Inspirador'  },
            { value: 'educativo',   label: 'Educativo'   },
            { value: 'casual',      label: 'Casual'      },
            { value: 'provocador',  label: 'Provocador'  },
          ]} />
        <Btn onClick={handleGenerar} disabled={loading || !tema.trim()}>
          {loading ? '⏳ Generando...' : '✨ Generar post'}
        </Btn>
      </Card>

      <Card>
        <h3 style={{ color: '#f0f0ff', marginBottom: 20, fontSize: 16 }}>📤 Publicar</h3>
        <Input label="Contenido del post" value={contenido} onChange={setContenido}
          placeholder="Escribe o genera el contenido aquí..." rows={6} />
        <MediaImageInput value={imagenNombre} onChange={setImagenNombre} plataformas={plataformas} />
        <PlatformToggle selected={plataformas} onChange={setPlataformas} />
        {msg && (
          <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
            background: msg.type === 'ok' ? '#1a3a2a' : '#3a1a1a',
            color: msg.type === 'ok' ? '#4ade80' : '#f87171',
            border: `1px solid ${msg.type === 'ok' ? '#2a5a3a' : '#5a2a2a'}` }}>
            {msg.text}
          </div>
        )}
        <Btn onClick={handlePublicar} disabled={publishing || !contenido.trim() || plataformas.length === 0}>
          {publishing ? '⏳ Publicando...' : '🚀 Publicar ahora'}
        </Btn>
      </Card>
    </div>
  );
}

// ─── Tab: Programar ──────────────────────────────────────────────────────────

function TabProgramar({ status }) {
  const [contenido, setContenido]       = useState('');
  const [plataformas, setPlataformas]   = useState(['linkedin']);
  const [imagenFuente, setImagenFuente] = useState('local');
  const [imagenNombre, setImagenNombre] = useState('');
  const [imagenPrompt, setImagenPrompt] = useState('');
  const [fecha, setFecha]               = useState('');
  const [loading, setLoading]           = useState(false);
  const [msg, setMsg]                   = useState(null);
  const [scheduled, setScheduled]       = useState([]);
  const [loadingList, setLoadingList]   = useState(true);

  const cargarProgramados = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await fetch(`${API}/social/scheduled?estado=pendiente`, { headers: authHeaders() });
      if (r.ok) setScheduled(await r.json());
    } catch {}
    setLoadingList(false);
  }, []);

  useEffect(() => { cargarProgramados(); }, [cargarProgramados]);

  async function handleProgramar() {
    if (!contenido.trim() || !fecha || plataformas.length === 0) return;
    setLoading(true); setMsg(null);
    try {
      const r = await fetch(`${API}/social/schedule`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          contenido, plataformas,
          imagen_fuente: imagenFuente,
          imagen_nombre: imagenNombre.trim() || null,
          imagen_prompt: imagenPrompt.trim() || null,
          programado_para: new Date(fecha).toISOString(),
        }),
      });
      const d = await r.json();
      if (r.ok) {
        setMsg({ type: 'ok', text: '✅ Post programado correctamente' });
        setContenido(''); setFecha(''); setImagenNombre(''); setImagenPrompt(''); setImagenFuente('local');
        cargarProgramados();
      } else {
        setMsg({ type: 'err', text: d.detail || 'Error programando post' });
      }
    } catch { setMsg({ type: 'err', text: 'Error de conexión' }); }
    setLoading(false);
  }

  async function handleCancelar(id) {
    try {
      await fetch(`${API}/social/scheduled/${id}`, { method: 'DELETE', headers: authHeaders() });
      cargarProgramados();
    } catch {}
  }

  async function handleDesprogramar(id) {
    try {
      await fetch(`${API}/social/scheduled/${id}/desprogramar`, { method: 'POST', headers: authHeaders() });
      cargarProgramados();
    } catch {}
  }

  const platformIcons = { linkedin: '💼', facebook: '📘', instagram: '📸' };

  return (
    <div>
      <Card style={{ marginBottom: 20 }}>
        <h3 style={{ color: '#f0f0ff', marginBottom: 20, fontSize: 16 }}>🗓️ Programar publicación</h3>
        <Input label="Contenido del post" value={contenido} onChange={setContenido}
          placeholder="Escribe el contenido a publicar..." rows={5} />
        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block', color:'#9b9fc4', fontSize:13, marginBottom:8 }}>🖼️ Fuente de imagen</label>
          <div style={{ display:'flex', gap:8, marginBottom:12 }}>
            {[['local','📁 Mis imágenes'],['pexels','📷 Pexels'],['ia','🤖 IA (Fal.ai)']].map(([val,lbl]) => (
              <button key={val} onClick={() => { setImagenFuente(val); setImagenNombre(''); setImagenPrompt(''); }} style={{
                flex:1, padding:'9px 0', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:13,
                background: imagenFuente===val ? '#3b0764' : '#12141f',
                border: `1px solid ${imagenFuente===val ? '#7c3aed' : '#2a2e4a'}`,
                color: imagenFuente===val ? '#e9d5ff' : '#9b9fc4',
              }}>{lbl}</button>
            ))}
          </div>
          {imagenFuente === 'local' && (
            <MediaImageInput value={imagenNombre} onChange={setImagenNombre} plataformas={plataformas} />
          )}
          {imagenFuente === 'pexels' && (
            <Input label="Keyword para Pexels (en inglés)" value={imagenNombre} onChange={setImagenNombre}
                   placeholder="Ej: modern office interior" />
          )}
          {imagenFuente === 'ia' && (
            <>
              <Input label="Prompt de imagen (en inglés)" value={imagenPrompt} onChange={setImagenPrompt}
                     placeholder="Ej: modern office interior, purple tones, flat design" />
              <div style={{ marginTop:8, padding:'8px 12px', background:'#2a1a0022', border:'1px solid #f59e0b44', borderRadius:6, fontSize:11, color:'#fbbf24' }}>
                ⚠️ Por ley (EU AI Act) las imágenes generadas por IA deben marcarse como tales al publicarse — esto es obligatorio y no se puede desactivar. Se añadirá automáticamente "[Imagen generada con Inteligencia Artificial]" al post. Si prefieres que no lleve esta etiqueta, sube tu propia foto en su lugar.
              </div>
            </>
          )}
        </div>
        <PlatformToggle selected={plataformas} onChange={setPlataformas} />
        <Input label="Fecha y hora de publicación" value={fecha} onChange={setFecha} type="datetime-local" />
        {msg && (
          <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
            background: msg.type === 'ok' ? '#1a3a2a' : '#3a1a1a',
            color: msg.type === 'ok' ? '#4ade80' : '#f87171',
            border: `1px solid ${msg.type === 'ok' ? '#2a5a3a' : '#5a2a2a'}` }}>
            {msg.text}
          </div>
        )}
        <Btn onClick={handleProgramar}
          disabled={loading || !contenido.trim() || !fecha || plataformas.length === 0}>
          {loading ? '⏳ Programando...' : '📅 Programar'}
        </Btn>
      </Card>

      <Card>
        <h3 style={{ color: '#f0f0ff', marginBottom: 16, fontSize: 16 }}>⏳ Programaciones pendientes</h3>
        {loadingList ? (
          <p style={{ color: '#5a5e82' }}>Cargando...</p>
        ) : scheduled.length === 0 ? (
          <p style={{ color: '#5a5e82', fontSize: 14 }}>No hay posts programados pendientes.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {scheduled.map(s => (
              <div key={s.id} style={{ background: '#12141f', border: '1px solid #2a2e4a',
                borderRadius: 8, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    {s.plataformas.map(p => <Badge key={p} color="blue">{platformIcons[p]} {p}</Badge>)}
                    {s.imagen_nombre && <Badge color="gray">☁️ {s.imagen_nombre}</Badge>}
                  </div>
                  <p style={{ color: '#f0f0ff', fontSize: 13, marginBottom: 6 }}>{s.contenido}</p>
                  <p style={{ color: '#5a5e82', fontSize: 12 }}>
                    📅 {new Date(s.programado_para).toLocaleString('es-ES')}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8, marginLeft: 16, flexShrink: 0 }}>
                  <Btn variant="secondary" onClick={() => handleDesprogramar(s.id)}>
                    ✏️ Editar
                  </Btn>
                  <Btn variant="danger" onClick={() => handleCancelar(s.id)}>
                    Cancelar
                  </Btn>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Tab: Historial ──────────────────────────────────────────────────────────

function TabHistorial() {
  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro]   = useState('');

  useEffect(() => {
    fetch(`${API}/social/history?limit=100`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(setPosts)
      .finally(() => setLoading(false));
  }, []);

  const filtrados = filtro ? posts.filter(p => p.plataforma === filtro) : posts;
  const platformIcons = { linkedin: '💼', facebook: '📘', instagram: '📸' };
  const estadoColor   = { publicado: 'green', fallido: 'red', borrador: 'gray' };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ color: '#f0f0ff', fontSize: 16 }}>📋 Historial de posts</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {['', 'linkedin', 'facebook', 'instagram'].map(p => (
            <Btn key={p} variant={filtro === p ? 'primary' : 'ghost'} onClick={() => setFiltro(p)}>
              {p ? `${platformIcons[p]} ${p}` : 'Todos'}
            </Btn>
          ))}
        </div>
      </div>
      {loading ? (
        <p style={{ color: '#5a5e82' }}>Cargando...</p>
      ) : filtrados.length === 0 ? (
        <p style={{ color: '#5a5e82', fontSize: 14 }}>Sin historial de posts aún.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtrados.map(p => (
            <div key={p.id} style={{ background: '#12141f', border: '1px solid #2a2e4a', borderRadius: 8, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Badge color="blue">{platformIcons[p.plataforma]} {p.plataforma}</Badge>
                  <Badge color={estadoColor[p.estado] || 'gray'}>{p.estado}</Badge>
                  {p.fuente !== 'manual' && <Badge color="yellow">{p.fuente}</Badge>}
                </div>
                <span style={{ color: '#5a5e82', fontSize: 12 }}>
                  {new Date(p.created_at).toLocaleString('es-ES')}
                </span>
              </div>
              <p style={{ color: '#f0f0ff', fontSize: 13, marginBottom: p.error_msg ? 6 : 0 }}>{p.contenido}</p>
              {p.error_msg && <p style={{ color: '#f87171', fontSize: 12, marginTop: 6 }}>⚠️ {p.error_msg}</p>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─── Tab: Credenciales ───────────────────────────────────────────────────────

function TabCredenciales({ status, onRefresh, oauthMsg, onClearMsg, pendingPages, onPageSelected }) {
  const [form, setForm] = useState({
    linkedin_token: '', linkedin_org_id: '',
    facebook_token: '', facebook_page_id: '', instagram_account_id: '',
  });
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState(null);
  const [pickerLoading, setPickerLoading] = useState(false);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  async function openDrivePicker() {
    setPickerLoading(true);
    try {
      const r = await fetch(`${API}/drive/picker-token?authorization=Bearer ${getToken()}`);
      if (!r.ok) throw new Error('No se pudo obtener token de Drive');
      const { access_token, api_key, app_id } = await r.json();

      await new Promise((resolve) => {
        if (window.gapi) return resolve();
        const s = document.createElement('script');
        s.src = 'https://apis.google.com/js/api.js';
        s.onload = resolve;
        document.head.appendChild(s);
      });

      await new Promise((resolve) => window.gapi.load('picker', resolve));

      const view = new window.google.picker.DocsView(window.google.picker.ViewId.FOLDERS)
        .setSelectFolderEnabled(true)
        .setMimeTypes('application/vnd.google-apps.folder');

      new window.google.picker.PickerBuilder()
        .addView(view)
        .setOAuthToken(access_token)
        .setDeveloperKey(api_key)
        .setAppId(app_id)
        .setTitle('Selecciona tu carpeta de imágenes')
        .setCallback(async (data) => {
          if (data.action === window.google.picker.Action.PICKED) {
            const folder = data.docs[0];
            await fetch(`${API}/drive/set-folder?authorization=Bearer ${getToken()}&folder_id=${folder.id}`, { method: 'POST' });
            onRefresh();
          }
        })
        .build()
        .setVisible(true);
    } catch (e) {
      alert('Error abriendo el selector: ' + e.message);
    } finally {
      setPickerLoading(false);
    }
  }

  function setField(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function handleSave() {
    setSaving(true); setMsg(null);
    const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v.trim() !== ''));
    try {
      const r = await fetch(`${API}/social/credentials`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (r.ok) {
        setMsg({ type: 'ok', text: '✅ Credenciales guardadas correctamente' });
        setForm({ linkedin_token: '', linkedin_org_id: '', facebook_token: '', facebook_page_id: '', instagram_account_id: '' });
        onRefresh();
      } else {
        setMsg({ type: 'err', text: d.detail || 'Error guardando' });
      }
    } catch { setMsg({ type: 'err', text: 'Error de conexión' }); }
    setSaving(false);
  }

  const netCard = (title, icon, connected, fields) => (
    <div style={{ background: '#12141f', border: `1px solid ${connected ? '#2a5a3a' : '#2a2e4a'}`,
      borderRadius: 10, padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ color: '#f0f0ff', fontWeight: 700, fontSize: 15 }}>{icon} {title}</span>
        <Badge color={connected ? 'green' : 'gray'}>{connected ? '✓ Conectado' : 'No conectado'}</Badge>
      </div>
      {fields}
    </div>
  );

  return (
    <div>
      {oauthMsg && (
        <div style={{ padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          background: oauthMsg.type === 'ok' ? '#1a3a2a' : '#3a1a1a',
          color:      oauthMsg.type === 'ok' ? '#4ade80'  : '#f87171',
          border:     `1px solid ${oauthMsg.type === 'ok' ? '#2a5a3a' : '#5a2a2a'}` }}>
          <span>{oauthMsg.text}</span>
          <button onClick={onClearMsg} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16, lineHeight: 1, marginLeft: 12 }}>✕</button>
        </div>
      )}
      <Card style={{ marginBottom: 20 }}>
        <h3 style={{ color: '#f0f0ff', marginBottom: 6, fontSize: 16 }}>🔑 Credenciales de Redes Sociales</h3>
        <p style={{ color: '#5a5e82', fontSize: 13, marginBottom: 24 }}>
          Los tokens se guardan de forma segura. Introduce solo los campos que quieras actualizar.
        </p>
        {netCard('LinkedIn', '💼', status?.linkedin_conectado, status?.linkedin_conectado ? (
          <div>
            <p style={{ color: '#5a5e82', fontSize: 12, marginBottom: status?.es_empresa ? 12 : 8 }}>
              {status?.es_empresa ? '✓ Conectado como empresa' : '✓ Conectado como perfil personal'}
              {status?.expira_en && <span style={{ color: '#fbbf24', marginLeft: 8 }}>
                · Expira {new Date(status.expira_en).toLocaleDateString('es-ES')}
              </span>}
            </p>
            {!status?.es_empresa && (
              <div style={{ padding: '8px 12px', background: '#2a2a0a', border: '1px solid #5a5a2a', borderRadius: 8, fontSize: 12, color: '#fbbf24', marginBottom: 12 }}>
                ⚠️ Estás conectado con un <strong>perfil personal</strong>, no con una página de empresa. Los posts se publicarán en tu perfil personal de LinkedIn. Para publicar como empresa, reconecta con una cuenta que administre una <a href="https://www.linkedin.com/help/linkedin/answer/a543852" target="_blank" rel="noreferrer" style={{ color: '#fbbf24' }}>Página de Empresa →</a>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={async () => {
                const ok = window.confirm(
                  '⚠️ Atención: reconectar LinkedIn sustituirá el token actual por uno nuevo que expira en 60 días.\n\n' +
                  'Si tu token actual fue generado desde el portal de desarrolladores (válido 2 años), perderás esa ventaja.\n\n' +
                  '¿Seguro que quieres continuar?'
                );
                if (!ok) return;
                const token = localStorage.getItem('devit_token');
                const r = await fetch(`${API}/linkedin/connect?authorization=Bearer ${token}`);
                const d = await r.json(); if (d.auth_url) window.location.href = d.auth_url;
              }} style={{ fontSize: 12, color: '#0a66c2', background: 'none', border: '1px solid #0a66c2', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>
                🔄 Reconectar
              </button>
              <button type="button" onClick={async () => {
                if (!window.confirm('¿Desconectar LinkedIn?')) return;
                const token = localStorage.getItem('devit_token');
                await fetch(`${API}/linkedin/disconnect?authorization=Bearer ${token}`, { method: 'DELETE' });
                onRefresh();
              }} style={{ fontSize: 12, color: '#f87171', background: 'none', border: '1px solid #f87171', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>
                🗑️ Desconectar
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p style={{ color: '#5a5e82', fontSize: 12, marginBottom: 12 }}>
              Conecta tu perfil o página de empresa de LinkedIn para publicar automáticamente.
            </p>
            <button type="button" onClick={async () => {
              const token = localStorage.getItem('devit_token');
              const r = await fetch(`${API}/linkedin/connect?authorization=Bearer ${token}`);
              const d = await r.json(); if (d.auth_url) window.location.href = d.auth_url;
            }} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#0a66c2', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              in Conectar con LinkedIn
            </button>
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#12141f', border: '1px solid #2a2e4a', borderRadius: 8, fontSize: 12, color: '#5a5e82', lineHeight: 1.6 }}>
              💡 <strong style={{ color: '#9b9fc4' }}>Para publicar como empresa</strong> necesitas tener una <strong style={{ color: '#9b9fc4' }}>Página de Empresa en LinkedIn</strong> y ser administrador de ella. Al conectar, el sistema detectará automáticamente si gestionas alguna página.{' '}
              <a href="https://www.linkedin.com/help/linkedin/answer/a543852" target="_blank" rel="noreferrer" style={{ color: '#0a66c2' }}>¿Cómo crear una página de empresa?</a>
            </div>
          </div>
        ))}
        {pendingPages.length > 0 && (
          <Card style={{ marginBottom: 16, border: '1px solid #1877F2' }}>
            <h3 style={{ color: '#f0f0ff', fontSize: 15, marginBottom: 8 }}>
              📋 Selecciona tu Página de Facebook
            </h3>
            <p style={{ color: '#5a5e82', fontSize: 12, marginBottom: 14 }}>
              Se encontraron {pendingPages.length} página{pendingPages.length > 1 ? 's' : ''} vinculada{pendingPages.length > 1 ? 's' : ''} a tu cuenta. Elige la que quieres conectar a Devit.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {pendingPages.map(page => (
                <button key={page.id} onClick={async () => {
                  const token = localStorage.getItem('devit_token');
                  const r = await fetch(`${API}/facebook/select-page?authorization=Bearer ${token}&page_id=${page.id}`, { method: 'POST' });
                  if (r.ok) {
                    onPageSelected();
                  }
                }} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 16px', borderRadius: 8, cursor: 'pointer',
                  background: '#12141f', border: '1px solid #2a2e4a', color: '#f0f0ff',
                  fontSize: 13, textAlign: 'left',
                }}>
                  <span><strong style={{ color: '#f0f0ff' }}>📘 {page.name}</strong></span>
                  <span style={{ color: '#5a5e82', fontSize: 11 }}>ID: {page.id}</span>
                </button>
              ))}
            </div>
          </Card>
        )}
        {netCard('Facebook & Instagram', '📘', status?.facebook_conectado, status?.facebook_conectado ? (
          <div>
            <p style={{ color: '#5a5e82', fontSize: 12, marginBottom: 12 }}>
              {status?.instagram_conectado
                ? <>✓ <strong style={{ color: '#f0f0ff' }}>{status.facebook_page_name || 'Facebook'}</strong> · ✓ Instagram{status.instagram_username ? <> <strong style={{ color: '#f0f0ff' }}>@{status.instagram_username}</strong></> : ' vinculado'}</>
                : <><strong style={{ color: '#f0f0ff' }}>{status.facebook_page_name || 'Facebook'}</strong> conectado · <span style={{ color: '#fbbf24' }}>⚠️ Instagram no detectado — asegúrate de que tu cuenta Instagram Business está vinculada a esta página de Facebook y haz Reconectar.</span></>
              }
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={async () => {
                const token = localStorage.getItem('devit_token');
                const r = await fetch(`${API}/facebook/connect?authorization=Bearer ${token}`);
                const d = await r.json(); if (d.auth_url) window.location.href = d.auth_url;
              }} style={{ fontSize: 12, color: '#1877F2', background: 'none', border: '1px solid #1877F2', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>
                🔄 Reconectar
              </button>
              <button type="button" onClick={async () => {
                if (!window.confirm('¿Desconectar Facebook e Instagram?')) return;
                const token = localStorage.getItem('devit_token');
                await fetch(`${API}/facebook/disconnect?authorization=Bearer ${token}`, { method: 'DELETE' });
                onRefresh();
              }} style={{ fontSize: 12, color: '#f87171', background: 'none', border: '1px solid #f87171', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>
                🗑️ Desconectar
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 16 }}>
              <p style={{ color: '#9b9fc4', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>🔑 Conexión manual</p>
              <p style={{ color: '#5a5e82', fontSize: 12, marginBottom: 10 }}>
                Pega tu <strong style={{ color: '#9b9fc4' }}>Page Access Token</strong> permanente de Facebook. Genera uno desde el <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer" style={{ color: '#1877F2' }}>Graph API Explorer</a> con los permisos <code style={{ color: '#fbbf24', fontSize: 11 }}>pages_manage_posts</code> y <code style={{ color: '#fbbf24', fontSize: 11 }}>pages_messaging</code>.
              </p>
              <div style={{ background: '#12141f', border: '1px solid #2a2e4a', borderRadius: 8, padding: '12px 14px', marginBottom: 14, fontSize: 12, color: '#5a5e82', lineHeight: 1.7 }}>
                <p style={{ color: '#9b9fc4', fontWeight: 600, marginBottom: 8 }}>📖 Cómo obtener el token paso a paso</p>
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                  <li>Ve al <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer" style={{ color: '#1877F2' }}>Graph API Explorer</a> e inicia sesión con tu cuenta de Facebook.</li>
                  <li>En el desplegable <strong style={{ color: '#9b9fc4' }}>Meta App</strong> (arriba a la derecha) selecciona una app tuya o usa <em>Graph API Explorer</em> (la de Meta por defecto).</li>
                  <li>Haz clic en <strong style={{ color: '#9b9fc4' }}>Add a Permission</strong> y añade: <code style={{ color: '#fbbf24' }}>pages_manage_posts</code>, <code style={{ color: '#fbbf24' }}>pages_messaging</code>, <code style={{ color: '#fbbf24' }}>instagram_basic</code>, <code style={{ color: '#fbbf24' }}>instagram_content_publish</code>.</li>
                  <li>Pulsa <strong style={{ color: '#9b9fc4' }}>Generate Access Token</strong> → acepta los permisos. Obtendrás un <em>User Token</em> corto (caduca en 1h).</li>
                  <li>Para que no caduque, canjéalo por un <strong style={{ color: '#9b9fc4' }}>Long-Lived Token</strong>: en el explorador escribe esta URL y ejecuta:<br />
                    <code style={{ color: '#fbbf24', wordBreak: 'break-all', display: 'block', marginTop: 4, background: '#0a0c14', padding: '4px 8px', borderRadius: 4 }}>
                      {'GET /oauth/access_token?grant_type=fb_exchange_token&client_id={APP_ID}&client_secret={APP_SECRET}&fb_exchange_token={TU_TOKEN}'}
                    </code>
                  </li>
                  <li>Con el Long-Lived Token llama a <code style={{ color: '#fbbf24' }}>GET /me/accounts</code> → obtendrás la lista de tus páginas. Copia el <strong style={{ color: '#9b9fc4' }}>Page Access Token</strong> (campo <code style={{ color: '#fbbf24' }}>access_token</code>) y el <strong style={{ color: '#9b9fc4' }}>Page ID</strong> (campo <code style={{ color: '#fbbf24' }}>id</code>) de tu página. Este token <em>no caduca</em>.</li>
                  <li><strong style={{ color: '#9b9fc4' }}>Para Instagram</strong>: llama a <code style={{ color: '#fbbf24' }}>GET /{'{page_id}'}?fields=instagram_business_account</code> usando el Page Access Token. El valor del campo <code style={{ color: '#fbbf24' }}>id</code> dentro de <code style={{ color: '#fbbf24' }}>instagram_business_account</code> es tu <strong style={{ color: '#9b9fc4' }}>Instagram Account ID</strong>.</li>
                </ol>
              </div>
              <input
                type="text"
                placeholder="Page Access Token (EAABx...)"
                value={form.facebook_token}
                onChange={e => setField('facebook_token', e.target.value)}
                style={{ width: '100%', background: '#1a1c2e', border: '1px solid #2a2e4a', borderRadius: 6, padding: '8px 12px', color: '#f0f0ff', fontSize: 13, marginBottom: 8, boxSizing: 'border-box' }}
              />
              <input
                type="text"
                placeholder="Page ID (ej: 100000000000001)"
                value={form.facebook_page_id}
                onChange={e => setField('facebook_page_id', e.target.value)}
                style={{ width: '100%', background: '#1a1c2e', border: '1px solid #2a2e4a', borderRadius: 6, padding: '8px 12px', color: '#f0f0ff', fontSize: 13, marginBottom: 8, boxSizing: 'border-box' }}
              />
              <input
                type="text"
                placeholder="Instagram Account ID (ej: 17841400000000001)"
                value={form.instagram_account_id}
                onChange={e => setField('instagram_account_id', e.target.value)}
                style={{ width: '100%', background: '#1a1c2e', border: '1px solid #2a2e4a', borderRadius: 6, padding: '8px 12px', color: '#f0f0ff', fontSize: 13, marginBottom: 4, boxSizing: 'border-box' }}
              />
              <p style={{ color: '#5a5e82', fontSize: 11, marginBottom: 12 }}>
                Necesario para publicar en Instagram. Si no lo rellenas, solo se publicará en Facebook.
              </p>
              <button type="button" onClick={handleSave} disabled={saving || !form.facebook_token.trim() || !form.facebook_page_id.trim()}
                style={{ fontSize: 13, fontWeight: 600, color: '#fff', background: '#1a3a2a', border: '1px solid #2a5a3a', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', opacity: (!form.facebook_token.trim() || !form.facebook_page_id.trim()) ? 0.5 : 1 }}>
                {saving ? '⏳ Guardando...' : '💾 Guardar tokens Facebook'}
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <div style={{ flex: 1, height: 1, background: '#2a2e4a' }} />
              <span style={{ color: '#5a5e82', fontSize: 11 }}>o conecta con OAuth</span>
              <div style={{ flex: 1, height: 1, background: '#2a2e4a' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <button type="button" onClick={async () => {
                const token = localStorage.getItem('devit_token');
                const r = await fetch(`${API}/facebook/connect?authorization=Bearer ${token}`);
                const d = await r.json(); if (d.auth_url) window.location.href = d.auth_url;
              }} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#1877F2', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                f Conectar con Facebook
              </button>
              <span style={{ fontSize: 11, color: '#fbbf24', background: '#2a2000', border: '1px solid #5a4a00', borderRadius: 4, padding: '3px 8px' }}>App en revisión</span>
            </div>
            <div style={{ padding: '10px 12px', background: '#12141f', border: '1px solid #2a2e4a', borderRadius: 8, fontSize: 12, color: '#5a5e82', lineHeight: 1.6 }}>
              💡 <strong style={{ color: '#9b9fc4' }}>Requisitos previos:</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 16 }}>
                <li>Tu <strong style={{ color: '#9b9fc4' }}>página de Facebook debe ser de empresa</strong> (no perfil personal). <a href="https://www.facebook.com/pages/create" target="_blank" rel="noreferrer" style={{ color: '#1877F2' }}>Crear página de empresa →</a></li>
                <li>Tu <strong style={{ color: '#9b9fc4' }}>cuenta de Instagram debe ser Business o Creator</strong> y estar vinculada a esa página de Facebook. <a href="https://help.instagram.com/570895513091153" target="_blank" rel="noreferrer" style={{ color: '#1877F2' }}>Cómo vincular Instagram a Facebook →</a></li>
              </ul>
              <p style={{ margin: '6px 0 0' }}>Al conectar por OAuth, el sistema detectará Instagram automáticamente si está vinculado.</p>
            </div>
          </div>
        ))}
        {msg && (
          <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
            background: msg.type === 'ok' ? '#1a3a2a' : '#3a1a1a',
            color: msg.type === 'ok' ? '#4ade80' : '#f87171',
            border: `1px solid ${msg.type === 'ok' ? '#2a5a3a' : '#5a2a2a'}` }}>
            {msg.text}
          </div>
        )}
        <Btn onClick={handleSave} disabled={saving}>
          {saving ? '⏳ Guardando...' : '💾 Guardar credenciales'}
        </Btn>
      </Card>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ color: '#f0f0ff', fontSize: 16, marginBottom: 6 }}>📁 Galería de imágenes</h3>
            <p style={{ color: '#5a5e82', fontSize: 13 }}>
              Sube tus imágenes directamente desde el editor de posts o desde el Plan de Contenido. Se almacenan en Devit y puedes reutilizarlas en cualquier publicación.
            </p>
          </div>
          <Badge color="green">✓ Activa</Badge>
        </div>
      </Card>
    </div>
  );
}

// ─── Tab: Plan de Contenido ──────────────────────────────────────────────────

const MESES = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'
];
const OBJETIVO_COLOR = { awareness:'blue', lead:'purple', venta:'green', confianza:'yellow', educacion:'gray' };
const RED_ICON = { linkedin:'💼', facebook:'📘', instagram:'📸' };

function ModalEditarBorrador({ post, onClose, onSave, canAprobar = false }) {
  const [contenido,     setContenido]     = useState(post.contenido || '');
  const [titulo,        setTitulo]        = useState(post.titulo    || '');
  const [imagenFuente,  setImagenFuente]  = useState(post.imagen_fuente === 'drive' ? 'local' : (post.imagen_fuente || 'local'));
  const [imagenNombre,  setImagenNombre]  = useState(post.imagen_nombre || '');
  const [imagenPrompt,  setImagenPrompt]  = useState(post.imagen_prompt || '');
  const [fecha,         setFecha]         = useState(() => {
    const d = new Date(post.programado_para);
    return d.toISOString().slice(0,16);
  });
  const [saving, setSaving] = useState(false);
  const [msg,    setMsg]    = useState(null);

  async function handleSave(aprobar = false) {
    setSaving(true); setMsg(null);
    const payload = { contenido, titulo, imagen_fuente: imagenFuente, imagen_nombre: imagenNombre || null,
                      imagen_prompt: imagenPrompt || null, programado_para: new Date(fecha).toISOString() };
    try {
      const r = await fetch(`${API}/social/scheduled/${post.id}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload),
      });
      if (!r.ok) { const d = await r.json(); setMsg({ type:'err', text: d.detail || 'Error guardando' }); setSaving(false); return; }
      if (aprobar) {
        const r2 = await fetch(`${API}/social/scheduled/${post.id}/approve`, { method:'POST', headers: authHeaders() });
        const d2 = await r2.json();
        if (d2.fal_sin_credito) setMsg({ type:'warn', text:'⚠️ Fal.ai sin créditos — se usará Pexels.' });
      }
      onSave();
      if (!aprobar) onClose();
    } catch { setMsg({ type:'err', text:'Error de conexión' }); }
    setSaving(false);
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'#1a1d2e', border:'1px solid #2a2e4a', borderRadius:14, padding:28, width:'100%', maxWidth:620, maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ color:'#f0f0ff', fontSize:16 }}>✏️ Editar borrador</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#9b9fc4', fontSize:20, cursor:'pointer' }}>✕</button>
        </div>

        <Input label="Título" value={titulo} onChange={setTitulo} placeholder="Título del post" />
        <Input label="Contenido" value={contenido} onChange={setContenido} rows={7}
               placeholder="Texto completo del post listo para publicar..." />

        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block', color:'#9b9fc4', fontSize:13, marginBottom:8 }}>🖼️ Fuente de imagen</label>
          <div style={{ display:'flex', gap:8, marginBottom:12 }}>
            {[['local','📁 Mis imágenes'],['pexels','📷 Pexels'],['ia','🤖 IA (Fal.ai)']].map(([val,lbl]) => (
              <button key={val} onClick={() => setImagenFuente(val)} style={{
                flex:1, padding:'9px 0', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:13,
                background: imagenFuente===val ? '#3b0764' : '#12141f',
                border: `1px solid ${imagenFuente===val ? '#7c3aed' : '#2a2e4a'}`,
                color: imagenFuente===val ? '#e9d5ff' : '#9b9fc4',
              }}>{lbl}</button>
            ))}
          </div>
          {imagenFuente === 'ia' && (
            <>
              <Input label="Prompt de imagen (en inglés)" value={imagenPrompt} onChange={setImagenPrompt}
                     placeholder="Ej: modern office interior, laptop, purple tones, flat design" />
              <div style={{ marginTop:8, padding:'8px 12px', background:'#2a1a0022', border:'1px solid #f59e0b44', borderRadius:6, fontSize:11, color:'#fbbf24' }}>
                ⚠️ Por ley (EU AI Act) las imágenes generadas por IA deben marcarse como tales al publicarse — esto es obligatorio y no se puede desactivar. Se añadirá automáticamente "[Imagen generada con Inteligencia Artificial]" al post. Si prefieres que no lleve esta etiqueta, sube tu propia foto en su lugar.
              </div>
            </>
          )}
          {imagenFuente === 'pexels' && (
            <Input label="Keyword para Pexels (en inglés)" value={imagenNombre} onChange={setImagenNombre}
                   placeholder="Ej: modern apartment interior" />
          )}
          {imagenFuente === 'local' && (
            <MediaImageInput value={imagenNombre} onChange={setImagenNombre} plataformas={[]} />
          )}
        </div>

        <Input label="Fecha de publicación" value={fecha} onChange={setFecha} type="datetime-local" />

        {msg && (
          <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:16, fontSize:13,
            background: msg.type==='ok' ? '#1a3a2a' : msg.type==='warn' ? '#3a2e1a' : '#3a1a1a',
            color: msg.type==='ok' ? '#4ade80' : msg.type==='warn' ? '#fbbf24' : '#f87171',
            border: `1px solid ${msg.type==='ok' ? '#2a5a3a' : msg.type==='warn' ? '#5a4a2a' : '#5a2a2a'}` }}>
            {msg.text}
          </div>
        )}

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Btn>
          <Btn variant="secondary" onClick={() => handleSave(false)} disabled={saving}>
            {saving ? '⏳...' : '💾 Guardar borrador'}
          </Btn>
          <Btn variant="teal" onClick={() => handleSave(true)} disabled={saving || !canAprobar}>
            {saving ? '⏳...' : '✅ Guardar y aprobar'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function TabPlan() {
  const [contexto,      setContexto]      = useState(null);
  const [loadingCtx,    setLoadingCtx]    = useState(true);
  const [fuenteForm,    setFuenteForm]    = useState('url');
  const [url,           setUrl]           = useState('');
  const [descripcion,   setDescripcion]   = useState('');
  const [onboarding,    setOnboarding]    = useState({ que_haces:'', que_vendes:'', cliente_ideal:'', diferenciador:'', tono:'profesional' });
  const [savingCtx,     setSavingCtx]     = useState(false);
  const [msgCtx,        setMsgCtx]        = useState(null);

  const now = new Date();
  const [mes,      setMes]      = useState(now.getMonth() + 1);
  const [anio,     setAnio]     = useState(now.getFullYear());
  const [numPosts, setNumPosts] = useState(20);
  const [redes,    setRedes]    = useState(['linkedin', 'facebook', 'instagram']);
  const [generando, setGenerando] = useState(false);
  const [msgPlan,   setMsgPlan]   = useState(null);
  const [jobFase,   setJobFase]   = useState(null);

  // CTA y catálogo
  const [opciones,          setOpciones]          = useState(null);
  const [ctaTipo,           setCtaTipo]           = useState('auto');
  const [ctaValor,          setCtaValor]          = useState('');
  const [incluirCatalogo,   setIncluirCatalogo]   = useState(false);
  const [propiedadesIds,    setPropiedadesIds]    = useState([]);
  const [regenerandoId,     setRegenerandoId]     = useState(null);
  const [temperatura,       setTemperatura]       = useState(0.75);
  const [longitud,          setLongitud]          = useState('estandar');

  const [planes,       setPlanes]       = useState([]);
  const [loadingPlanes, setLoadingPlanes] = useState(true);
  const [planActivo,   setPlanActivo]   = useState(null);
  const [borradores,   setBorradores]   = useState([]);
  const [pendientesPlan, setPendientesPlan] = useState([]);
  const [desprogramandoTodo, setDesprogramandoTodo] = useState(false);
  const [desprogramandoId,   setDesprogramandoId]   = useState(null);
  const [aprobandoTodo, setAprobandoTodo] = useState(false);
  const [editando,     setEditando]     = useState(null);  // post a editar
  const [confirmAprobacion, setConfirmAprobacion] = useState(false);
  const [etiquetaIA,   setEtiquetaIA]   = useState(false);

  const cargarContexto = useCallback(async () => {
    setLoadingCtx(true);
    try {
      const r = await fetch(`${API}/social/context`, { headers: authHeaders() });
      if (r.ok) { const d = await r.json(); setContexto(d.tiene_contexto ? d.contexto : null); }
    } catch {}
    setLoadingCtx(false);
  }, []);

  const cargarPlanes = useCallback(async () => {
    setLoadingPlanes(true);
    try {
      const r = await fetch(`${API}/social/plan`, { headers: authHeaders() });
      if (r.ok) setPlanes(await r.json());
    } catch {}
    setLoadingPlanes(false);
  }, []);

  const cargarBorradores = useCallback(async (planId) => {
    try {
      const r = await fetch(`${API}/social/scheduled?estado=borrador`, { headers: authHeaders() });
      if (r.ok) {
        const all = await r.json();
        setBorradores(planId ? all.filter(b => b.plan_id === planId) : all);
      }
    } catch {}
  }, []);

  const cargarPendientesPlan = useCallback(async (planId) => {
    try {
      const r = await fetch(`${API}/social/scheduled?estado=pendiente`, { headers: authHeaders() });
      if (r.ok) {
        const all = await r.json();
        setPendientesPlan(planId ? all.filter(p => p.plan_id === planId) : []);
      }
    } catch {}
  }, []);

  useEffect(() => { cargarContexto(); cargarPlanes(); }, [cargarContexto, cargarPlanes]);

  useEffect(() => {
    fetch(`${API}/social/plan/options`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setOpciones(d); if (!d.tiene_whatsapp) setCtaTipo('web'); } })
      .catch(() => {});
  }, []);

  async function handleGuardarContexto() {
    setSavingCtx(true); setMsgCtx(null);
    const payload = {};
    if (fuenteForm === 'url') payload.url = url.trim();
    else if (fuenteForm === 'descripcion') payload.descripcion = descripcion.trim();
    else payload.respuestas_onboarding = onboarding;
    try {
      const r = await fetch(`${API}/social/context/analyze`, { method:'POST', headers: authHeaders(), body: JSON.stringify(payload) });
      const d = await r.json();
      if (r.ok) { setMsgCtx({ type:'ok', text:`✅ Contexto guardado (${d.tokens_used} tokens)` }); cargarContexto(); }
      else setMsgCtx({ type:'err', text: d.detail || 'Error analizando' });
    } catch { setMsgCtx({ type:'err', text:'Error de conexión' }); }
    setSavingCtx(false);
  }

  async function handleGenerarPlan() {
    if (!contexto) return;
    setGenerando(true); setMsgPlan(null); setJobFase(null);
    try {
      const r = await fetch(`${API}/social/plan/generate`, {
        method:'POST', headers: authHeaders(),
        body: JSON.stringify({
          mes, anio, num_posts: numPosts, redes_activas: redes,
          cta_tipo: ctaTipo,
          cta_valor: ctaValor.trim() || null,
          incluir_catalogo: incluirCatalogo,
          propiedades_ids: (incluirCatalogo && propiedadesIds.length > 0) ? propiedadesIds : null,
          temperatura,
          longitud,
        }),
      });
      const d = await r.json();
      if (!r.ok) { setMsgPlan({ type:'err', text: d.detail || 'Error generando el plan' }); setGenerando(false); return; }

      // ── Background job (num_posts > BATCH_SIZE) ─────────────────────────
      if (d.en_proceso && d.job_id) {
        setJobFase(d.fase || 'Iniciando...');
        await new Promise((resolve, reject) => {
          const interval = setInterval(async () => {
            try {
              const rs = await fetch(`${API}/social/plan/generate/status/${d.job_id}`, { headers: authHeaders() });
              const s = await rs.json();
              setJobFase(s.fase || '...');
              if (s.estado === 'completado') {
                clearInterval(interval);
                // Cargar el plan generado
                const rPlan = await fetch(`${API}/social/plan/${s.plan_id}`, { headers: authHeaders() });
                if (rPlan.ok) { const pd = await rPlan.json(); setPlanActivo(pd); cargarBorradores(s.plan_id); cargarPendientesPlan(s.plan_id); }
                cargarPlanes();
                setMsgPlan({ type:'ok', text:`✅ Plan generado. Puedes verlo aquí abajo en "Mis planes" 👇` });
                setTimeout(() => document.getElementById('mis-planes-section')?.scrollIntoView({ behavior:'smooth' }), 300);
                resolve();
              } else if (s.estado === 'error') {
                clearInterval(interval);
                setMsgPlan({ type:'err', text: s.error || 'Error generando el plan' });
                reject();
              }
            } catch { clearInterval(interval); reject(); }
          }, 3000);
        });
        setJobFase(null);
        setGenerando(false);
        return;
      }

      // ── Síncrono (≤ BATCH_SIZE posts) ───────────────────────────────────
      const numB = d.borradores_creados ?? d.total_posts ?? '';
      setMsgPlan({ type:'ok', text:`✅ Plan generado${numB ? ` — ${numB} posts` : ''}. Puedes verlo aquí abajo en "Mis planes" 👇` });
      await cargarPlanes();
      const rPlan = await fetch(`${API}/social/plan/${d.id}`, { headers: authHeaders() });
      if (rPlan.ok) { const pd = await rPlan.json(); setPlanActivo(pd); cargarBorradores(d.id); cargarPendientesPlan(d.id); }
      setTimeout(() => document.getElementById('mis-planes-section')?.scrollIntoView({ behavior:'smooth' }), 300);
    } catch { setMsgPlan({ type:'err', text:'Error de conexión' }); }
    setJobFase(null);
    setGenerando(false);
  }

  async function handleVerPlan(planId) {
    try {
      const r = await fetch(`${API}/social/plan/${planId}`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setPlanActivo(d);
        cargarBorradores(planId);
        cargarPendientesPlan(planId);
      }
    } catch {}
  }

  useEffect(() => {
    if (planActivo) {
      setTimeout(() => document.getElementById('plan-posts-section')?.scrollIntoView({ behavior:'smooth' }), 100);
    }
  }, [planActivo?.id]);

  async function handleEliminarPost(planId, postId) {
    try {
      await fetch(`${API}/social/plan/${planId}/post/${postId}`, { method:'DELETE', headers: authHeaders() });
      handleVerPlan(planId);
    } catch {}
  }

  async function handleAprobarTodo() {
    if (!planActivo || !confirmAprobacion) return;
    setAprobandoTodo(true);
    try {
      const r = await fetch(`${API}/social/plan/${planActivo.id}/approve`, {
        method:'POST', headers: authHeaders(),
        body: JSON.stringify({ ajustes: null, etiqueta_ia: etiquetaIA }),
      });
      const d = await r.json();
      if (r.ok) {
        setMsgPlan({
          type: d.fal_sin_credito ? 'warn' : 'ok',
          text: d.mensaje,
        });
        setConfirmAprobacion(false);
        setEtiquetaIA(false);
        cargarPlanes(); handleVerPlan(planActivo.id);
      } else setMsgPlan({ type:'err', text: d.detail || 'Error aprobando' });
    } catch { setMsgPlan({ type:'err', text:'Error de conexión' }); }
    setAprobandoTodo(false);
  }

  async function handleAprobarUno(postId) {
    if (!confirmAprobacion) return;
    try {
      const r = await fetch(`${API}/social/scheduled/${postId}/approve`, { method:'POST', headers: authHeaders() });
      const d = await r.json();
      if (r.ok) {
        if (d.fal_sin_credito) setMsgPlan({ type:'warn', text:'⚠️ Fal.ai sin créditos — se usará Pexels para este post.' });
        if (planActivo) { cargarBorradores(planActivo.id); cargarPendientesPlan(planActivo.id); }
      }
    } catch {}
  }

  async function handleDesprogramarTodo() {
    if (!planActivo) return;
    setDesprogramandoTodo(true);
    try {
      const r = await fetch(`${API}/social/plan/${planActivo.id}/desprogramar-todos`, { method:'POST', headers: authHeaders() });
      const d = await r.json();
      if (r.ok) {
        setMsgPlan({ type:'ok', text: `↩️ ${d.count} posts pasados a borrador — ya puedes editarlos.` });
        cargarPlanes(); handleVerPlan(planActivo.id);
      } else setMsgPlan({ type:'err', text: d.detail || 'Error desprogramando' });
    } catch { setMsgPlan({ type:'err', text:'Error de conexión' }); }
    setDesprogramandoTodo(false);
  }

  async function handleDesprogramarUno(postId) {
    setDesprogramandoId(postId);
    try {
      const r = await fetch(`${API}/social/scheduled/${postId}/desprogramar`, { method:'POST', headers: authHeaders() });
      if (r.ok && planActivo) { cargarBorradores(planActivo.id); cargarPendientesPlan(planActivo.id); cargarPlanes(); }
    } catch {}
    setDesprogramandoId(null);
  }

  async function handleRegenerarUno(postId) {
    setRegenerandoId(postId);
    try {
      const r = await fetch(`${API}/social/scheduled/${postId}/regenerar`, { method:'POST', headers: authHeaders() });
      const d = await r.json();
      if (r.ok) {
        if (planActivo) cargarBorradores(planActivo.id);
        setMsgPlan({ type:'ok', text: `✅ Post regenerado${d.estilo_aplicado ? ' con tu estilo' : ''} · ${d.tokens_usados} tokens` });
      } else {
        setMsgPlan({ type:'err', text: d.detail || 'Error regenerando el post' });
      }
    } catch { setMsgPlan({ type:'err', text:'Error de conexión' }); }
    setRegenerandoId(null);
  }

  const platformIcons = { linkedin:'💼', facebook:'📘', instagram:'📸' };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* Contexto del negocio */}
      <Card>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
          <div>
            <h3 style={{ color:'#f0f0ff', fontSize:16, marginBottom:4 }}>🏢 Contexto de tu negocio</h3>
            <p style={{ color:'#5a5e82', fontSize:13 }}>La IA necesita conocer tu negocio para generar contenido relevante.</p>
          </div>
          {!loadingCtx && contexto && <Badge color="green">✓ Configurado</Badge>}
        </div>
        {!loadingCtx && contexto && (
          <div style={{ background:'#12141f', border:'1px solid #2a5a3a', borderRadius:10, padding:16, marginBottom:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
              <span style={{ color:'#4ade80', fontWeight:700, fontSize:13 }}>{(contexto.nombre_negocio && contexto.nombre_negocio !== 'Sin nombre detectado') ? contexto.nombre_negocio : 'Tu negocio'}</span>
              <Badge color="blue">{contexto.sector}</Badge>
            </div>
            <p style={{ color:'#9b9fc4', fontSize:13 }}>{contexto.descripcion_corta}</p>
          </div>
        )}
        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block', color:'#9b9fc4', fontSize:13, marginBottom:8 }}>
            {contexto ? 'Actualizar contexto:' : '¿Cómo conocemos tu negocio?'}
          </label>
          <div style={{ display:'flex', gap:8, marginBottom:16 }}>
            {[['url','🌐 Mi web'],['descripcion','✍️ Lo describo'],['onboarding','📋 Formulario']].map(([id,lbl]) => (
              <button key={id} onClick={() => setFuenteForm(id)} style={{
                flex:1, padding:'9px 0', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:13,
                background: fuenteForm===id ? '#3b0764' : '#12141f',
                border: `1px solid ${fuenteForm===id ? '#7c3aed' : '#2a2e4a'}`,
                color: fuenteForm===id ? '#e9d5ff' : '#9b9fc4',
              }}>{lbl}</button>
            ))}
          </div>
          {fuenteForm === 'url' && <Input value={url} onChange={setUrl} placeholder="https://tunegocio.com" label="URL de tu web" />}
          {fuenteForm === 'descripcion' && <Input value={descripcion} onChange={setDescripcion} rows={4} label="Describe tu negocio" placeholder="Ej: Somos una clínica dental en Madrid..." />}
          {fuenteForm === 'onboarding' && (
            <>
              <Input label="¿A qué se dedica?" value={onboarding.que_haces} onChange={v => setOnboarding(o=>({...o,que_haces:v}))} placeholder="Ej: Clínica dental con 3 sedes en Madrid" />
              <Input label="¿Qué ofreces?" value={onboarding.que_vendes} onChange={v => setOnboarding(o=>({...o,que_vendes:v}))} placeholder="Ej: Ortodoncia, blanqueamiento, implantes" />
              <Input label="¿Cliente ideal?" value={onboarding.cliente_ideal} onChange={v => setOnboarding(o=>({...o,cliente_ideal:v}))} placeholder="Ej: Adultos 30-50 años, profesionales" />
              <Input label="¿Qué te diferencia?" value={onboarding.diferenciador} onChange={v => setOnboarding(o=>({...o,diferenciador:v}))} placeholder="Ej: Tratamientos express sin listas de espera" />
              <Select label="Tono" value={onboarding.tono} onChange={v => setOnboarding(o=>({...o,tono:v}))}
                options={[{value:'profesional',label:'Profesional'},{value:'cercano',label:'Cercano'},{value:'inspirador',label:'Inspirador'},{value:'divertido',label:'Divertido'},{value:'tecnico',label:'Técnico'}]} />
            </>
          )}
        </div>
        {msgCtx && <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:16, fontSize:13,
          background: msgCtx.type==='ok' ? '#1a3a2a' : '#3a1a1a', color: msgCtx.type==='ok' ? '#4ade80' : '#f87171',
          border: `1px solid ${msgCtx.type==='ok' ? '#2a5a3a' : '#5a2a2a'}` }}>{msgCtx.text}</div>}
        <Btn onClick={handleGuardarContexto} disabled={savingCtx}>
          {savingCtx ? '⏳ Analizando...' : contexto ? '🔄 Actualizar' : '🔍 Analizar y guardar'}
        </Btn>
      </Card>

      {/* Generar plan */}
      <Card style={{ opacity: contexto ? 1 : 0.5 }}>
        <h3 style={{ color:'#f0f0ff', fontSize:16, marginBottom:4 }}>📅 Generar plan mensual</h3>
        <p style={{ color:'#5a5e82', fontSize:13, marginBottom:20 }}>
          {contexto ? 'La IA generará el contenido completo de cada post. Podrás revisar y editar antes de aprobar.' : '⚠️ Configura primero el contexto.'}
        </p>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginBottom:16 }}>
          <Select label="Mes" value={mes} onChange={v => setMes(Number(v))} options={MESES.map((m,i) => ({ value:i+1, label:m }))} />
          <Select label="Año" value={anio} onChange={v => setAnio(Number(v))} options={[2025,2026,2027].map(y=>({value:y,label:y}))} />
          <Select label="Posts" value={numPosts} onChange={v => setNumPosts(Number(v))} options={[10,12,15,20,24,28,30].map(n=>({value:n,label:`${n} posts`}))} />
        </div>
        <PlatformToggle selected={redes} onChange={setRedes} />

        {/* CTA */}
        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block', color:'#9b9fc4', fontSize:13, marginBottom:8 }}>
            📣 CTA de los posts — ¿a dónde lleva la llamada a la acción?
          </label>
          <div style={{ display:'flex', gap:8, marginBottom:10, flexWrap:'wrap' }}>
            {[
              ['auto',    '⚡ Automático'],
              ['whatsapp','💬 WhatsApp'],
              ['web',     '🌐 Mi web'],
              ['link',    '🔗 Link propio'],
              ['ninguno', '🚫 Sin CTA'],
            ].map(([val, lbl]) => {
              const disabled = val === 'whatsapp' && opciones && !opciones.tiene_whatsapp;
              return (
                <button key={val} onClick={() => !disabled && setCtaTipo(val)} style={{
                  padding:'7px 14px', borderRadius:7, cursor: disabled ? 'not-allowed' : 'pointer',
                  fontWeight:600, fontSize:12, opacity: disabled ? 0.4 : 1,
                  background: ctaTipo===val ? '#3b0764' : '#12141f',
                  border: `1px solid ${ctaTipo===val ? '#7c3aed' : '#2a2e4a'}`,
                  color: ctaTipo===val ? '#e9d5ff' : '#9b9fc4',
                }} title={disabled ? 'Requiere plan con WhatsApp' : ''}>
                  {lbl}
                </button>
              );
            })}
          </div>
          {ctaTipo === 'auto' && (
            <p style={{ color:'#5a5e82', fontSize:12 }}>
              {opciones?.tiene_whatsapp
                ? `✓ Usará WhatsApp: wa.me/${opciones.whatsapp_numero}`
                : '⚠️ Sin WhatsApp configurado — los posts no incluirán CTA de contacto'}
            </p>
          )}
          {(ctaTipo === 'web' || ctaTipo === 'link') && (
            <input value={ctaValor} onChange={e => setCtaValor(e.target.value)}
              placeholder={ctaTipo==='web' ? 'https://tunegocio.com' : 'Link o número de contacto'}
              style={{ width:'100%', background:'#12141f', border:'1px solid #2a2e4a',
                borderRadius:8, padding:'9px 14px', color:'#f0f0ff', fontSize:13,
                outline:'none', boxSizing:'border-box' }} />
          )}
          {ctaTipo === 'whatsapp' && opciones?.tiene_whatsapp && (
            <p style={{ color:'#5a5e82', fontSize:12 }}>✓ wa.me/{opciones.whatsapp_numero}</p>
          )}
        </div>

        {/* Catálogo */}
        {opciones?.tiene_catalogo && (
          <div style={{ marginBottom:16 }}>
            <div style={{ padding:'12px 16px', borderRadius:8,
              background: incluirCatalogo ? 'rgba(124,77,255,0.08)' : '#12141f',
              border: `1px solid ${incluirCatalogo ? 'rgba(124,77,255,0.4)' : '#2a2e4a'}` }}>
              <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                <input type="checkbox" checked={incluirCatalogo}
                  onChange={e => { setIncluirCatalogo(e.target.checked); if (!e.target.checked) setPropiedadesIds([]); }}
                  style={{ width:16, height:16, cursor:'pointer' }} />
                <div>
                  <span style={{ color:'#f0f0ff', fontSize:13, fontWeight:600 }}>
                    {opciones.catalogo_tipo === 'inmobiliaria' ? '🏠 Incluir propiedades en el plan' : '🛍️ Incluir productos en el plan'}
                  </span>
                  <p style={{ color:'#5a5e82', fontSize:12, margin:'2px 0 0' }}>
                    {opciones.catalogo_tipo === 'inmobiliaria'
                      ? 'El plan incluirá posts con pisos y propiedades reales de tu cartera'
                      : 'El plan incluirá posts con productos concretos de tu catálogo'}
                  </p>
                </div>
              </label>
            </div>

            {/* Selector de propiedades específicas */}
            {incluirCatalogo && opciones.catalogo_tipo === 'inmobiliaria' && opciones.propiedades?.length > 0 && (
              <div style={{ marginTop:10, padding:'12px 16px', borderRadius:8, background:'#12141f', border:'1px solid #2a2e4a' }}>
                <p style={{ color:'#9b9fc4', fontSize:12, marginBottom:10 }}>
                  Selecciona las propiedades a destacar en el plan. Si no seleccionas ninguna, se elegirán automáticamente.
                  {propiedadesIds.length > 0 && <span style={{ color:'#c4b5fd', fontWeight:600 }}> · {propiedadesIds.length} seleccionadas</span>}
                </p>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:8 }}>
                  {opciones.propiedades.map(p => {
                    const sel = propiedadesIds.includes(p.id);
                    return (
                      <div key={p.id} onClick={() => setPropiedadesIds(ids => sel ? ids.filter(i => i !== p.id) : [...ids, p.id])}
                        style={{ borderRadius:8, overflow:'hidden', border:`2px solid ${sel ? '#7c3aed' : '#2a2e4a'}`,
                          cursor:'pointer', background: sel ? 'rgba(124,58,237,0.1)' : '#1a1d2e' }}>
                        {p.foto_url
                          ? <img src={p.foto_url} alt={p.tipo} style={{ width:'100%', height:90, objectFit:'cover', display:'block' }} />
                          : <div style={{ width:'100%', height:90, background:'#2a2e4a', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28 }}>🏠</div>
                        }
                        <div style={{ padding:'6px 8px' }}>
                          <p style={{ color:'#f0f0ff', fontSize:11, fontWeight:600, margin:0 }}>{p.tipo} · {p.zona}</p>
                          {p.precio && <p style={{ color:'#c4b5fd', fontSize:11, margin:'2px 0 0' }}>{p.precio}</p>}
                          {sel && <p style={{ color:'#7c3aed', fontSize:10, margin:'2px 0 0', fontWeight:700 }}>✓ Seleccionada</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        {/* Creatividad */}
        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block', color:'#9b9fc4', fontSize:13, marginBottom:8 }}>🎨 Creatividad del contenido</label>
          <div style={{ display:'flex', gap:8 }}>
            {[
              { val:0.5,  icon:'🛡️', label:'Seguro',      desc:'Predecible y coherente. Ideal para sectores formales. Puede sonar repetitivo.' },
              { val:0.75, icon:'⚖️', label:'Equilibrado', desc:'Buen balance entre creatividad y coherencia. Recomendado.' },
              { val:0.92, icon:'🔥', label:'Atrevido',    desc:'Ganchos más originales. Riesgo: puede salirse del tono ocasionalmente.' },
            ].map(({ val, icon, label, desc }) => (
              <button key={val} onClick={() => setTemperatura(val)} title={desc} style={{
                flex:1, padding:'10px 8px', borderRadius:8, cursor:'pointer', textAlign:'center',
                background: temperatura===val ? '#3b0764' : '#12141f',
                border: `1px solid ${temperatura===val ? '#7c3aed' : '#2a2e4a'}`,
                color: temperatura===val ? '#e9d5ff' : '#9b9fc4',
              }}>
                <div style={{ fontSize:18, marginBottom:4 }}>{icon}</div>
                <div style={{ fontSize:12, fontWeight:700 }}>{label}</div>
              </button>
            ))}
          </div>
          <p style={{ color:'#5a5e82', fontSize:11, marginTop:6 }}>
            {temperatura===0.5 && '🛡️ Seguro: texto predecible y coherente, ideal para sectores formales. Puede sonar repetitivo entre planes.'}
            {temperatura===0.75 && '⚖️ Equilibrado: buen balance entre creatividad y coherencia. Recomendado para la mayoría de negocios.'}
            {temperatura===0.92 && '🔥 Atrevido: ganchos más originales e inesperados. Riesgo: puede desviarse del tono o del tema ocasionalmente.'}
          </p>
        </div>

        {/* Longitud */}
        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block', color:'#9b9fc4', fontSize:13, marginBottom:8 }}>📏 Longitud de los posts</label>
          <div style={{ display:'flex', gap:8 }}>
            {[
              { val:'breve',    icon:'✂️', label:'Breve',    desc:'LinkedIn 80-100 p · Instagram 40-60 p · Facebook 60-80 p' },
              { val:'estandar', icon:'📄', label:'Estándar', desc:'LinkedIn 150-200 p · Instagram 80-120 p · Facebook 100-140 p' },
              { val:'detallado',icon:'📖', label:'Detallado',desc:'LinkedIn 250-300 p · Instagram 140-170 p · Facebook 170-210 p' },
            ].map(({ val, icon, label, desc }) => (
              <button key={val} onClick={() => setLongitud(val)} title={desc} style={{
                flex:1, padding:'10px 8px', borderRadius:8, cursor:'pointer', textAlign:'center',
                background: longitud===val ? '#0f3a35' : '#12141f',
                border: `1px solid ${longitud===val ? '#2dd4bf' : '#2a2e4a'}`,
                color: longitud===val ? '#2dd4bf' : '#9b9fc4',
              }}>
                <div style={{ fontSize:18, marginBottom:4 }}>{icon}</div>
                <div style={{ fontSize:12, fontWeight:700 }}>{label}</div>
              </button>
            ))}
          </div>
          <p style={{ color:'#5a5e82', fontSize:11, marginTop:6 }}>
            {longitud==='breve' && '✂️ Breve: LinkedIn 80-100 · Instagram 40-60 · Facebook 60-80 palabras'}
            {longitud==='estandar' && '📄 Estándar: LinkedIn 150-200 · Instagram 80-120 · Facebook 100-140 palabras'}
            {longitud==='detallado' && '📖 Detallado: LinkedIn 250-300 · Instagram 140-170 · Facebook 170-210 palabras'}
          </p>
        </div>

        {msgPlan && <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:16, fontSize:13,
          background: msgPlan.type==='ok' ? '#1a3a2a' : msgPlan.type==='warn' ? '#3a2e1a' : '#3a1a1a',
          color: msgPlan.type==='ok' ? '#4ade80' : msgPlan.type==='warn' ? '#fbbf24' : '#f87171',
          border:`1px solid ${msgPlan.type==='ok' ? '#2a5a3a' : msgPlan.type==='warn' ? '#5a4a2a' : '#5a2a2a'}` }}>{msgPlan.text}</div>}
        <Btn onClick={handleGenerarPlan} disabled={generando || !contexto || redes.length===0}>
          {generando ? (jobFase || '⏳ Generando contenido completo...') : '✨ Generar plan del mes'}
        </Btn>
      </Card>

      {/* Lista de planes */}
      {!loadingPlanes && planes.length > 0 && (
        <Card id="mis-planes-section">
          <h3 style={{ color:'#f0f0ff', fontSize:16, marginBottom:16 }}>📋 Mis planes</h3>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {planes.map(p => (
              <div key={p.id} style={{
                background:'#12141f', border:`1px solid ${planActivo?.id===p.id ? '#7c3aed' : '#2a2e4a'}`,
                borderRadius:8, padding:16, display:'flex', justifyContent:'space-between', alignItems:'center',
              }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', gap:8, marginBottom:6, alignItems:'center', flexWrap:'wrap' }}>
                    <span style={{ color:'#f0f0ff', fontWeight:700, fontSize:14 }}>{p.mes} {p.anio}</span>
                    <Badge color={p.estado==='aprobado' ? 'green' : p.estado==='borrador' ? 'yellow' : 'gray'}>{p.estado}</Badge>
                    {p.total_posts && <span style={{ color:'#5a5e82', fontSize:12 }}>{p.total_posts} posts</span>}
                  </div>
                  {p.resumen_estrategia && <p style={{ color:'#9b9fc4', fontSize:12, marginBottom:0 }}>{p.resumen_estrategia.slice(0,100)}...</p>}
                </div>
                <button onClick={() => handleVerPlan(p.id)} style={{
                  marginLeft:12, padding:'8px 14px', borderRadius:8, border:'1px solid #7c3aed',
                  background: planActivo?.id===p.id ? '#7c3aed' : 'transparent',
                  color: planActivo?.id===p.id ? '#fff' : '#c4b5fd',
                  cursor:'pointer', fontWeight:600, fontSize:13, whiteSpace:'nowrap',
                }}>
                  {planActivo?.id===p.id ? '✓ Abierto' : 'Ver posts →'}
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Borradores del plan activo */}
      {planActivo && (
        <Card id="plan-posts-section">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
            <div>
              <h3 style={{ color:'#f0f0ff', fontSize:16, marginBottom:4 }}>
                {planActivo.plan?.mes} {planActivo.plan?.anio}
              </h3>
              {planActivo.plan?.resumen_estrategia && (
                <p style={{ color:'#9b9fc4', fontSize:13 }}>{planActivo.plan.resumen_estrategia}</p>
              )}
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <Badge color={planActivo.estado==='aprobado' ? 'green' : planActivo.estado==='borrador' ? 'yellow' : 'gray'}>
                {planActivo.estado}
              </Badge>
              {(planActivo.estado === 'borrador' || borradores.length > 0) && (
                <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
                  <label style={{ display:'flex', alignItems:'flex-start', gap:6, cursor:'pointer', color:'#9b9fc4', fontSize:12, maxWidth:300, textAlign:'left' }}>
                    <input type="checkbox" checked={confirmAprobacion} onChange={e => setConfirmAprobacion(e.target.checked)} style={{ marginTop:2, flexShrink:0 }} />
                    <span>He revisado el contenido y asumo la responsabilidad editorial <span style={{ color:'#5a5e82' }}>(EU AI Act Art. 50)</span></span>
                  </label>
                  <label style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', color:'#9b9fc4', fontSize:12 }}>
                    <input type="checkbox" checked={etiquetaIA} onChange={e => setEtiquetaIA(e.target.checked)} />
                    <span>Añadir <span style={{ color:'#c4b5fd' }}>[Generado con IA]</span> al final de cada post</span>
                  </label>
                  <Btn variant="teal" onClick={handleAprobarTodo} disabled={aprobandoTodo || !confirmAprobacion}>
                    {aprobandoTodo ? '⏳ Aprobando...' : `✅ Aprobar todos (${borradores.length})`}
                  </Btn>
                </div>
              )}
            </div>
          </div>

          {/* Lista de borradores */}
          {borradores.length > 0 ? (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {borradores.map(b => (
                <div key={b.id} style={{ background:'#12141f', border:'1px solid #2a2e4a', borderRadius:10, padding:16 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                    <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                      <span style={{ color:'#f0f0ff', fontWeight:700, fontSize:13 }}>
                        {b.plataformas?.map(p => platformIcons[p] || p).join(' ')}
                        {' '}{new Date(b.programado_para).toLocaleDateString('es-ES', { day:'numeric', month:'short' })}
                      </span>
                      <Badge color={OBJETIVO_COLOR['awareness'] || 'gray'}>{b.plataformas?.[0]}</Badge>
                      {b.editado && <Badge color="yellow">✏️ editado</Badge>}
                      <span style={{ color:'#5a5e82', fontSize:11 }} title={b.imagen_nombre || ''}>
                        {b.imagen_fuente==='ia' ? '🤖 IA' : b.imagen_fuente==='local' ? '📁 Local' : '📷 Pexels'}
                        {b.imagen_nombre ? ` · ${truncNombre(b.imagen_nombre)}` : ''}
                      </span>
                    </div>
                    <div style={{ display:'flex', gap:6 }}>
                      <button onClick={() => setEditando(b)} style={{
                        background:'#1a1d2e', border:'1px solid #2a2e4a', borderRadius:6, padding:'5px 12px',
                        color:'#9b9fc4', cursor:'pointer', fontSize:12, fontWeight:600 }}>✏️ Editar</button>
                      <button
                        onClick={() => handleRegenerarUno(b.id)}
                        disabled={regenerandoId === b.id}
                        style={{ background:'#1a1d2e', border:'1px solid #2a2e4a', borderRadius:6, padding:'5px 12px',
                          color: regenerandoId === b.id ? '#5a5e82' : '#c4b5fd', cursor: regenerandoId === b.id ? 'not-allowed' : 'pointer',
                          fontSize:12, fontWeight:600, opacity: regenerandoId === b.id ? 0.6 : 1 }}>
                        {regenerandoId === b.id ? '⏳...' : '🔄 Regenerar'}
                      </button>
                      <button onClick={() => handleAprobarUno(b.id)} disabled={!confirmAprobacion} style={{
                        background: confirmAprobacion ? '#0f3a35' : '#1a1d2e', border:`1px solid ${confirmAprobacion ? '#1a5a50' : '#2a2e4a'}`, borderRadius:6, padding:'5px 12px',
                        color: confirmAprobacion ? '#2dd4bf' : '#3a3e5a', cursor: confirmAprobacion ? 'pointer' : 'not-allowed', fontSize:12, fontWeight:600,
                        opacity: confirmAprobacion ? 1 : 0.5 }}>✓ Aprobar</button>
                    </div>
                  </div>
                  {b.titulo && <p style={{ color:'#c4b5fd', fontSize:13, fontWeight:600, marginBottom:6 }}>{b.titulo}</p>}
                  <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
                    {b.imagen_fuente === 'propiedad' && b.imagen_url && (
                      <img src={b.imagen_url} alt="propiedad" style={{ width:80, height:60, objectFit:'cover', borderRadius:6, flexShrink:0, border:'1px solid #2a2e4a' }} />
                    )}
                    <p style={{ color:'#9b9fc4', fontSize:13, lineHeight:1.6, flex:1, whiteSpace:'pre-wrap' }}>
                      {b.contenido}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : planActivo.estado === 'aprobado' ? (
            <div style={{ background:'#1a3a2a', border:'1px solid #2a5a3a', borderRadius:8, padding:16, textAlign:'center', color:'#4ade80', fontSize:13 }}>
              ✓ Plan aprobado — todos los posts están programados para publicación automática
            </div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid #2a2e4a' }}>
                    {['Día','Red','Objetivo','Título','Imagen',''].map(h => (
                      <th key={h} style={{ textAlign:'left', padding:'8px 12px', color:'#5a5e82', fontWeight:600, fontSize:11, textTransform:'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(planActivo.plan?.posts || []).map((post, idx) => (
                    <tr key={post.id} style={{ borderBottom:'1px solid #1a1d2e', background: idx%2===0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                      <td style={{ padding:'10px 12px', color:'#f0f0ff', fontWeight:700 }}>{post.dia}</td>
                      <td style={{ padding:'10px 12px' }}>{RED_ICON[post.red]||'📡'} {post.red}</td>
                      <td style={{ padding:'10px 12px' }}><Badge color={OBJETIVO_COLOR[post.objetivo]||'gray'}>{post.objetivo}</Badge></td>
                      <td style={{ padding:'10px 12px', color:'#9b9fc4', maxWidth:200 }}>{post.titulo}</td>
                      <td style={{ padding:'10px 12px', color:'#5a5e82', fontSize:12 }}>{post.imagen_sugerida?.slice(0,20) || '—'}</td>
                      <td style={{ padding:'10px 12px' }}>
                        {planActivo.estado === 'borrador' && (
                          <button onClick={() => handleEliminarPost(planActivo.id, post.id)}
                            style={{ background:'transparent', border:'none', cursor:'pointer', color:'#5a5e82', fontSize:16 }}>✕</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Pendientes del plan activo — permite desprogramar (volver a borrador) */}
      {planActivo && pendientesPlan.length > 0 && (
        <Card style={{ marginTop:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <h3 style={{ color:'#f0f0ff', fontSize:16 }}>📅 Programados de este plan ({pendientesPlan.length})</h3>
            <Btn variant="secondary" onClick={handleDesprogramarTodo} disabled={desprogramandoTodo}>
              {desprogramandoTodo ? '⏳ Desprogramando...' : `↩️ Desprogramar todos (${pendientesPlan.length})`}
            </Btn>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {pendientesPlan.map(p => (
              <div key={p.id} style={{ background:'#12141f', border:'1px solid #2a2e4a', borderRadius:10, padding:16,
                display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', gap:8, marginBottom:8, flexWrap:'wrap' }}>
                    {p.plataformas?.map(pl => <Badge key={pl} color="blue">{platformIcons[pl] || pl} {pl}</Badge>)}
                  </div>
                  {p.titulo && <p style={{ color:'#c4b5fd', fontSize:13, fontWeight:600, marginBottom:4 }}>{p.titulo}</p>}
                  <p style={{ color:'#5a5e82', fontSize:12 }}>
                    📅 {new Date(p.programado_para).toLocaleString('es-ES')}
                  </p>
                </div>
                <button onClick={() => handleDesprogramarUno(p.id)} disabled={desprogramandoId === p.id} style={{
                  background:'#1a1d2e', border:'1px solid #2a2e4a', borderRadius:6, padding:'5px 12px', marginLeft:16, flexShrink:0,
                  color: desprogramandoId === p.id ? '#5a5e82' : '#9b9fc4', cursor: desprogramandoId === p.id ? 'not-allowed' : 'pointer',
                  fontSize:12, fontWeight:600 }}>
                  {desprogramandoId === p.id ? '⏳...' : '↩️ Desprogramar'}
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Modal de edición */}
      {editando && (
        <ModalEditarBorrador
          post={editando}
          onClose={() => setEditando(null)}
          onSave={() => { setEditando(null); if (planActivo) cargarBorradores(planActivo.id); }}
          canAprobar={confirmAprobacion}
        />
      )}
    </div>
  );
}


// ─── Página principal ────────────────────────────────────────────────────────

export default function Social() {
  const [tab, setTab]           = useState('plan');
  const [status, setStatus]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [oauthMsg, setOauthMsg]         = useState(null);
  const [pendingPages, setPendingPages] = useState([]);

  const cargarStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API}/social/status`, { headers: authHeaders() });
      if (r.ok) setStatus(await r.json());
      else if (r.status === 403) setStatus({ modulo_social: false });
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { cargarStatus(); }, [cargarStatus]);

  // Detectar resultado OAuth en URL y abrir tab Credenciales
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fb = params.get('facebook');
    const li = params.get('linkedin');
    if (fb || li) {
      setTab('credenciales');
      if (fb === 'select_page') {
        fetch(`${API}/facebook/pending-pages?authorization=Bearer ${localStorage.getItem('devit_token')}`)
          .then(r => r.ok ? r.json() : { pages: [] })
          .then(d => setPendingPages(d.pages || []));
      }
      if (fb === 'no_pages')   setOauthMsg({ type: 'err', text: '❌ No se encontró ninguna Página de Empresa vinculada a tu cuenta de Facebook. Crea una página de empresa primero y vuelve a intentarlo.' });
      if (fb === 'error')      setOauthMsg({ type: 'err', text: '❌ Error al conectar con Facebook. Inténtalo de nuevo.' });
      if (li === 'connected')  setOauthMsg({ type: 'ok',  text: '✅ LinkedIn conectado correctamente.' });
      if (li === 'error')      setOauthMsg({ type: 'err', text: '❌ Error al conectar con LinkedIn. Inténtalo de nuevo.' });
      // Limpiar params de la URL sin recargar
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const tabs = [
    { id: 'plan',         label: '🗓️ Plan de Contenido' },
    { id: 'generar',      label: '✨ Generar & Publicar' },
    { id: 'programar',    label: '📅 Programación' },
    { id: 'historial',    label: '📋 Historial' },
    { id: 'credenciales', label: '🔗 Conexiones' },
  ];

  if (loading) {
    return (
      <div style={{ padding: 32, color: '#9b9fc4', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span>⏳</span> Cargando módulo social...
      </div>
    );
  }

  if (!status?.modulo_social) {
    return (
      <div style={{ padding: 32, maxWidth: 560 }}>
        <h1 style={{ color: '#f0f0ff', fontSize: 24, fontWeight: 800, marginBottom: 8 }}>
          📣 Publicador Social
        </h1>
        <p style={{ color: '#5a5e82', marginBottom: 32 }}>
          Genera y publica contenido en LinkedIn, Facebook e Instagram con IA.
        </p>
        <Card>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
            <h2 style={{ color: '#f0f0ff', fontSize: 20, marginBottom: 12 }}>Módulo no activado</h2>
            <p style={{ color: '#9b9fc4', marginBottom: 24, fontSize: 14 }}>
              El Publicador Social está disponible en los planes Web+Social, WhatsApp+Social y Completo+Social.
            </p>
            <Btn onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'billing' }))}>
              Ver planes y precios
            </Btn>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ color: '#f0f0ff', fontSize: 24, fontWeight: 800, marginBottom: 6 }}>
            📣 Publicador Social
          </h1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {status.linkedin_conectado  && <Badge color="blue">💼 LinkedIn</Badge>}
            {status.facebook_conectado  && <Badge color="blue">📘 Facebook</Badge>}
            {status.instagram_conectado && <Badge color="blue">📸 Instagram</Badge>}
            <Badge color="green">📁 Galería local activa</Badge>
            {!status.linkedin_conectado && !status.facebook_conectado && !status.instagram_conectado && (
              <Badge color="yellow">⚠️ Sin redes conectadas — ve a Credenciales</Badge>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #2a2e4a' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '10px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 14,
            background: 'transparent', border: 'none',
            borderBottom: `2px solid ${tab === t.id ? '#7c3aed' : 'transparent'}`,
            color: tab === t.id ? '#c4b5fd' : '#9b9fc4', marginBottom: -1,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'generar'      && <TabGenerar status={status} />}
      {tab === 'programar'    && <TabProgramar status={status} />}
      {tab === 'plan'         && <TabPlan />}
      {tab === 'historial'    && <TabHistorial />}
      {tab === 'credenciales' && <TabCredenciales status={status} onRefresh={cargarStatus} oauthMsg={oauthMsg} onClearMsg={() => setOauthMsg(null)} pendingPages={pendingPages} onPageSelected={() => { setPendingPages([]); cargarStatus(); }} />}
    </div>
  );
}